import {
  emptyCharacterCatalogSnapshot,
  emptyCharacterPresenceSnapshot,
  normalizeCharacterCatalogSnapshot,
  normalizeCharacterPresenceSnapshot,
} from "./map-character-presence-contract.js";

const DATASTAR_FETCH_EVENT = "datastar-fetch";
const DATASTAR_PRESENCE_PATCH_EVENT = "datastar-patch-signals";
const DATASTAR_UNAVAILABLE_EVENTS = new Set([
  "started",
  "error",
  "retrying",
  "retries-failed",
  "finished",
]);
const CHARACTER_STREAM_HEALTH_VERSION = 1;

function hasOwn(value, key) {
  return Boolean(value) && Object.prototype.hasOwnProperty.call(value, key);
}

function scheduleLatestFrame(callback) {
  const root =
    typeof globalThis.window?.requestAnimationFrame === "function"
      ? globalThis.window
      : globalThis;
  if (typeof root.requestAnimationFrame === "function") {
    const id = root.requestAnimationFrame.call(root, callback);
    return { kind: "raf", id, root };
  }
  const token = { kind: "microtask", cancelled: false };
  const run = () => {
    if (!token.cancelled) {
      callback();
    }
  };
  if (typeof globalThis.queueMicrotask === "function") {
    globalThis.queueMicrotask(run);
  } else {
    Promise.resolve().then(run);
  }
  return token;
}

function cancelScheduledFrame(token) {
  if (!token) {
    return;
  }
  if (token.kind === "raf") {
    token.root.cancelAnimationFrame?.(token.id);
  } else {
    token.cancelled = true;
  }
}

export function characterBridgeInputPatchFromSignals(signals = {}) {
  return {
    characterCatalog: normalizeCharacterCatalogSnapshot(
      characterCatalogSnapshotFromDatastarSignals(
        signals?._map_character_catalog ?? emptyCharacterCatalogSnapshot(),
      ),
    ),
    characterPresence: normalizeCharacterPresenceSnapshot(
      signals?._map_character_presence ?? emptyCharacterPresenceSnapshot(),
    ),
  };
}

// Datastar's reactive Proxy can materialize a missing optional property as an
// own empty-string signal when any presentation consumer reads it. Detach each
// entry from that Proxy and project framework placeholders back to omission at
// this one boundary. The public catalog normalizer remains strict for wire
// values passed to it directly.
function characterCatalogSnapshotFromDatastarSignals(value) {
  if (!value || typeof value !== "object" || !Array.isArray(value.characters)) {
    return value;
  }
  const characters = value.characters.map((character) => {
    if (!character || typeof character !== "object") {
      return character;
    }
    const projected = { ...character };
    if (
      !hasOwn(character, "characterClassKey") ||
      projected.characterClassKey === "" ||
      projected.characterClassKey === null ||
      projected.characterClassKey === undefined
    ) {
      delete projected.characterClassKey;
    }
    return projected;
  });
  return { ...value, characters };
}

export function patchTouchesCharacterBridgeSignals(patch) {
  return (
    hasOwn(patch, "_map_character_catalog") ||
    hasOwn(patch, "_map_character_presence")
  );
}

function normalizeCharacterStreamHealth(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError("Character stream health must be an object");
  }
  const unsupported = Object.keys(value).find(
    (key) => key !== "version" && key !== "validForMs",
  );
  if (unsupported) {
    throw new TypeError(
      `Character stream health contains unsupported field ${unsupported}`,
    );
  }
  if (value.version !== CHARACTER_STREAM_HEALTH_VERSION) {
    throw new TypeError(
      `Character stream health version must be ${CHARACTER_STREAM_HEALTH_VERSION}`,
    );
  }
  if (!Number.isSafeInteger(value.validForMs) || value.validForMs <= 0) {
    throw new TypeError("Character stream health validForMs must be a positive safe integer");
  }
  return {
    version: CHARACTER_STREAM_HEALTH_VERSION,
    validForMs: value.validForMs,
  };
}

function characterStreamPatchFromDatastarFetchEvent(event, streamElement) {
  const detail = event?.detail;
  if (
    detail?.el !== streamElement ||
    detail?.type !== DATASTAR_PRESENCE_PATCH_EVENT ||
    typeof detail?.argsRaw?.signals !== "string"
  ) {
    return null;
  }
  const patch = JSON.parse(detail.argsRaw.signals);
  const hasPresence = hasOwn(patch, "_map_character_presence");
  const hasHealth = hasOwn(patch, "_map_character_stream");
  if (!hasPresence && !hasHealth) {
    return null;
  }
  if (hasPresence && !hasHealth) {
    throw new TypeError("Character presence patch requires stream health");
  }
  return {
    health: hasHealth
      ? normalizeCharacterStreamHealth(patch._map_character_stream)
      : null,
    presence: hasPresence
      ? normalizeCharacterPresenceSnapshot(patch._map_character_presence)
      : null,
  };
}

function normalizedPresenceForStreamAvailability(snapshot, available) {
  if (available) {
    return snapshot;
  }
  return {
    ...snapshot,
    characters: snapshot.characters.map((character) =>
      character.status === "live"
        ? { ...character, status: "stale" }
        : character
    ),
  };
}

export function characterPresenceSnapshotForStreamAvailability(value, available) {
  return normalizedPresenceForStreamAvailability(
    normalizeCharacterPresenceSnapshot(value),
    available,
  );
}

export function createCharacterPresenceStreamHealthController({
  streamElement,
  eventTarget = globalThis.document,
  onAvailabilityChange = () => {},
  onPresenceSnapshot = () => {},
  scheduleExpiration = globalThis.setTimeout?.bind(globalThis),
  cancelExpiration = globalThis.clearTimeout?.bind(globalThis),
} = {}) {
  if (!streamElement) {
    throw new TypeError("Character presence stream health requires its Datastar element");
  }
  if (
    typeof eventTarget?.addEventListener !== "function" ||
    typeof eventTarget?.removeEventListener !== "function"
  ) {
    throw new TypeError("Character presence stream health requires an event target");
  }
  if (
    typeof onAvailabilityChange !== "function" ||
    typeof onPresenceSnapshot !== "function" ||
    typeof scheduleExpiration !== "function" ||
    typeof cancelExpiration !== "function"
  ) {
    throw new TypeError("Character presence stream health callbacks must be functions");
  }

  let available = false;
  let bound = false;
  let disposed = false;
  let latestPresence = null;
  let expiration = null;
  let expirationGeneration = 0;

  function cancelWatchdog() {
    expirationGeneration += 1;
    if (expiration === null) {
      return;
    }
    cancelExpiration(expiration);
    expiration = null;
  }

  function armWatchdog(validForMs) {
    cancelWatchdog();
    const generation = expirationGeneration;
    expiration = scheduleExpiration(() => {
      if (generation !== expirationGeneration) {
        return;
      }
      expiration = null;
      setAvailable(false);
    }, validForMs);
  }

  function setAvailable(next) {
    if (available === next) {
      return false;
    }
    available = next;
    onAvailabilityChange(available);
    return true;
  }

  function handleEvent(event) {
    if (disposed || event?.detail?.el !== streamElement) {
      return false;
    }
    const patch = characterStreamPatchFromDatastarFetchEvent(event, streamElement);
    if (patch) {
      if (patch.health) {
        armWatchdog(patch.health.validForMs);
      }
      if (patch.presence) {
        latestPresence = patch.presence;
        setAvailable(true);
        onPresenceSnapshot(patch.presence);
      }
      return true;
    }
    if (!DATASTAR_UNAVAILABLE_EVENTS.has(event?.detail?.type)) {
      return false;
    }
    cancelWatchdog();
    setAvailable(false);
    return true;
  }

  function start() {
    if (disposed || bound) {
      return false;
    }
    eventTarget.addEventListener(DATASTAR_FETCH_EVENT, handleEvent);
    bound = true;
    return true;
  }

  function dispose() {
    if (disposed) {
      return;
    }
    disposed = true;
    if (bound) {
      eventTarget.removeEventListener(DATASTAR_FETCH_EVENT, handleEvent);
      bound = false;
    }
    cancelWatchdog();
    setAvailable(false);
    latestPresence = null;
  }

  return Object.freeze({
    dispose,
    handleEvent,
    isAvailable: () => available,
    latestPresenceSnapshot: () => latestPresence,
    start,
  });
}

export function createCharacterPresenceBridgeController({
  bridge,
  readSignals,
  streamAvailable = false,
  scheduleFrame = scheduleLatestFrame,
  cancelFrame = cancelScheduledFrame,
} = {}) {
  if (typeof bridge?.setState !== "function") {
    throw new TypeError("Character presence bridge controller requires bridge.setState");
  }
  if (typeof readSignals !== "function") {
    throw new TypeError("Character presence bridge controller requires readSignals");
  }

  let scheduledPresence = null;
  let pendingPresence = null;
  let authoritativePresence = null;
  let presenceStreamAvailable = streamAvailable === true;
  let disposed = false;

  function submitCatalog(value) {
    if (disposed) {
      return null;
    }
    const characterCatalog = normalizeCharacterCatalogSnapshot(value);
    return bridge.setState({ characterCatalog });
  }

  function flushPresence() {
    scheduledPresence = null;
    const characterPresence = pendingPresence;
    pendingPresence = null;
    if (disposed || !characterPresence) {
      return null;
    }
    return bridge.setState({ characterPresence });
  }

  function submitPresence(value) {
    if (disposed) {
      return false;
    }
    authoritativePresence = normalizeCharacterPresenceSnapshot(value);
    pendingPresence = normalizedPresenceForStreamAvailability(
      authoritativePresence,
      presenceStreamAvailable,
    );
    if (!scheduledPresence) {
      scheduledPresence = scheduleFrame(flushPresence);
    }
    return true;
  }

  function setStreamAvailable(available) {
    if (disposed || presenceStreamAvailable === (available === true)) {
      return false;
    }
    presenceStreamAvailable = available === true;
    if (authoritativePresence) {
      submitPresence(authoritativePresence);
    }
    return true;
  }

  function submitSignalPatch(patch) {
    if (!patchTouchesCharacterBridgeSignals(patch)) {
      return false;
    }
    const signals = readSignals();
    if (!signals || typeof signals !== "object" || Array.isArray(signals)) {
      throw new TypeError("Character presence bridge readSignals must return an object");
    }
    if (hasOwn(patch, "_map_character_catalog")) {
      submitCatalog(
        characterCatalogSnapshotFromDatastarSignals(signals._map_character_catalog),
      );
    }
    if (hasOwn(patch, "_map_character_presence")) {
      submitPresence(signals._map_character_presence);
    }
    return true;
  }

  function dispose() {
    disposed = true;
    pendingPresence = null;
    authoritativePresence = null;
    cancelFrame(scheduledPresence);
    scheduledPresence = null;
  }

  return Object.freeze({
    dispose,
    flushPresence,
    setStreamAvailable,
    submitCatalog,
    submitPresence,
    submitSignalPatch,
  });
}
